import React, { useState, useEffect } from 'react';
import { Menu, Rocket } from 'lucide-react';
import { Image } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import Logo from '../../assets/img/zeronine.png';

const Navigation = () => {
   const [scrolled, setScrolled] = useState(false);
   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
   const [activeSection, setActiveSection] = useState('home');
   const [isLoggedIn, setIsLoggedIn] = useState(false);

   // Get current page from URL
   const getCurrentPage = () => {
      const path = window.location.pathname;
      if (path.includes('/about')) return 'about';
      if (path.includes('/')) return 'home';
      return 'home';
   };

   const [currentPage, setCurrentPage] = useState(getCurrentPage());

   // Check login status
   useEffect(() => {
      const tokenData = localStorage.getItem('authTokenCustomer');
      setIsLoggedIn(!!tokenData); // Convert to boolean
   }, []);

   useEffect(() => {
      const handleScroll = () => {
         const isScrolled = window.scrollY > 10;
         setScrolled(isScrolled);

         // Only detect sections if on home page
         if (currentPage === 'home') {
            const sections = ['features', 'pricing', 'testimonials'];
            const scrollPosition = window.scrollY + 100;

            for (const section of sections) {
               const element = document.getElementById(section);
               if (element) {
                  const offsetTop = element.offsetTop;
                  const offsetBottom = offsetTop + element.offsetHeight;

                  if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
                     setActiveSection(section);
                     return;
                  }
               }
            }

            // If no section is active and at top, set to home
            if (window.scrollY < 100) {
               setActiveSection('home');
            }
         }
      };

      window.addEventListener('scroll', handleScroll);
      handleScroll(); // Initial check
      return () => window.removeEventListener('scroll', handleScroll);
   }, [currentPage]);

   // Update active section when page changes
   useEffect(() => {
      const page = getCurrentPage();
      setCurrentPage(page);
      setActiveSection(page);
   }, []);

   const navigateToPage = (page) => {
      setCurrentPage(page);
      setActiveSection(page);
      setMobileMenuOpen(false);

      // Navigate to the route
      if (page === 'home') {
         window.location.href = '/';
      } else if (page === 'about') {
         window.location.href = '/about';
      }
   };

   const scrollToSection = (section) => {
      setMobileMenuOpen(false);

      // If not on home page, navigate to home first
      if (currentPage !== 'home') {
         window.location.href = `/#${section}`;
         return;
      }

      // If on home page, scroll to section
      setActiveSection(section);
      const element = document.getElementById(section);
      if (element) {
         element.scrollIntoView({ behavior: 'smooth' });
      }
   };

   const handleStartTrial = () => {
      console.log('Start free trial clicked');
      // window.location.href = '/auth/sign-in';
      window.location.href = '/admin/default';
   };

   const handleLogin = () => {
      window.location.href = '/auth/sign-in';
   };

   const handleRegistration = () => {
      window.location.href = '/auth/registration';
   };

   const isActive = (item) => {
      return activeSection === item;
   };

   const getNavButtonClasses = (item) => {
      return `px-5 py-2.5 rounded-xl font-semibold transition-all ${
         isActive(item)
            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg scale-105'
            : 'text-gray-400 hover:bg-gray-100 hover:scale-105'
      }`;
   };

   const getMobileNavButtonClasses = (item) => {
      return `block w-full text-left px-4 py-3 rounded-xl font-semibold transition-all ${
         isActive(item)
            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
            : 'text-gray-700 hover:bg-gray-100'
      }`;
   };

   return (
      <nav
         className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
            scrolled ? 'bg-white/95 backdrop-blur-2xl shadow-2xl border-b border-gray-200' : 'bg-transparent'
         }`}>
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
               <button onClick={() => navigateToPage('home')} className="flex items-center gap-3 group">
                  <Link to="/" className="flex items-center gap-3 group">
                     <div className="relative">
                        <Image src={Logo} alt="ZeroNine Logo" width={'200px'} />
                     </div>
                  </Link>
               </button>

               {/* Desktop Navigation */}
               <div className="hidden lg:flex items-center space-x-2">
                  <button onClick={() => navigateToPage('home')} className={getNavButtonClasses('home')}>
                     Home
                  </button>
                  <button onClick={() => navigateToPage('about')} className={getNavButtonClasses('about')}>
                     About
                  </button>
                  <button onClick={() => scrollToSection('features')} className={getNavButtonClasses('features')}>
                     Features
                  </button>
                  <button onClick={() => scrollToSection('pricing')} className={getNavButtonClasses('pricing')}>
                     Pricing
                  </button>
                  <button
                     onClick={() => scrollToSection('testimonials')}
                     className={getNavButtonClasses('testimonials')}>
                     Reviews
                  </button>

                  <div className="h-8 w-px bg-gray-300 mx-2"></div>

                  {/* Conditional rendering based on login status */}
                  {isLoggedIn ? (
                     // Show only "Go To Zeronine" button when logged in
                     <button
                        onClick={handleStartTrial}
                        className="relative group px-7 py-3 rounded-xl font-bold overflow-hidden transition-all hover:scale-105">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 transition-all"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-all"></div>
                        <span className="relative text-white flex items-center gap-2">Go To Zeronine</span>
                     </button>
                  ) : (
                     // Show Login, Registration, and Start Free Trial when not logged in
                     <>
                        <button
                           onClick={handleLogin}
                           className="px-5 py-2.5 rounded-xl text-gray-400 hover:bg-gray-100 font-semibold transition-all hover:scale-105">
                           Login
                        </button>
                        {/* <button
                           onClick={handleRegistration}
                           className="px-5 py-2.5 rounded-xl text-gray-700 hover:bg-gray-100 font-semibold transition-all hover:scale-105">
                           Registration
                        </button> */}
                     </>
                  )}
               </div>

               {/* Mobile menu button */}
               <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2">
                  <Menu className="w-7 h-7 text-gray-700" />
               </button>
            </div>
         </div>

         {/* Mobile Navigation */}
         {mobileMenuOpen && (
            <div className="lg:hidden bg-white/95 backdrop-blur-2xl border-t border-gray-200 shadow-2xl">
               <div className="px-4 py-6 space-y-2">
                  <button onClick={() => navigateToPage('home')} className={getMobileNavButtonClasses('home')}>
                     Home
                  </button>
                  <button onClick={() => navigateToPage('about')} className={getMobileNavButtonClasses('about')}>
                     About Us
                  </button>
                  <button onClick={() => scrollToSection('features')} className={getMobileNavButtonClasses('features')}>
                     Features
                  </button>
                  <button onClick={() => scrollToSection('pricing')} className={getMobileNavButtonClasses('pricing')}>
                     Pricing
                  </button>
                  <button
                     onClick={() => scrollToSection('testimonials')}
                     className={getMobileNavButtonClasses('testimonials')}>
                     Reviews
                  </button>

                  {/* Conditional rendering for mobile */}
                  {isLoggedIn ? (
                     // Show only "Go To Zeronine" button when logged in
                     <div className="pt-4">
                        <button
                           onClick={handleStartTrial}
                           className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 rounded-xl font-bold hover:shadow-lg transition-all">
                           Go To Zeronine
                        </button>
                     </div>
                  ) : (
                     // Show Login and Start Free Trial when not logged in
                     <div className="flex gap-2 pt-4">
                        <button
                           onClick={handleLogin}
                           className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all">
                           Login
                        </button>
                        <button
                           onClick={handleStartTrial}
                           className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 rounded-xl font-bold hover:shadow-lg transition-all">
                           Start Free Trial
                        </button>
                     </div>
                  )}
               </div>
            </div>
         )}
      </nav>
   );
};

export default Navigation;
